import type {
  BugReport,
  BugReportStatus,
  BugReportSubmitInput,
  BugSlaTier,
} from '../types/bugReport';
import { authedFetch } from '../lib/authedFetch';

export type { BugReportSubmitInput };

async function readErrorMessage(res: Response, fallback: string): Promise<string> {
  let bodyText = '';
  try {
    bodyText = await res.text();
  } catch {
    return `${fallback}: ${res.status}`;
  }
  if (!bodyText.trim()) return `${fallback}: ${res.status}`;
  try {
    const parsed = JSON.parse(bodyText) as { error?: string; message?: string };
    const detail = parsed.error ?? parsed.message ?? bodyText;
    return `${fallback}: ${res.status} ${detail}`;
  } catch {
    return `${fallback}: ${res.status} ${bodyText}`;
  }
}

export async function submitBugReport(input: BugReportSubmitInput): Promise<string> {
  const res = await authedFetch('/api/bug-reports', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(await readErrorMessage(res, 'Submit failed'));
  const { bugReportId } = await res.json() as { bugReportId: string };
  return bugReportId;
}

export async function fetchBugQueue(): Promise<BugReport[]> {
  const res = await authedFetch('/api/bug-reports/admin/queue');
  if (!res.ok) throw new Error(await readErrorMessage(res, 'Queue fetch failed'));
  return await res.json() as BugReport[];
}

export interface BugStatusUpdateBody {
  status?: BugReportStatus;
  assigneeUid?: string | null;
  slaTier?: BugSlaTier;
  triageNote?: string;
  duplicateOfId?: string;
  resolutionNote?: string;
}

export async function updateBugReport(
  bugReportId: string,
  body: BugStatusUpdateBody,
): Promise<void> {
  const res = await authedFetch(`/api/bug-reports/admin/${encodeURIComponent(bugReportId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readErrorMessage(res, 'Bug report update failed'));
}

export async function updateBugStatus(
  bugReportId: string,
  status: BugReportStatus,
  extras?: Omit<BugStatusUpdateBody, 'status'>,
): Promise<void> {
  await updateBugReport(bugReportId, { status, ...extras });
}

/** R-131-SUP-E */
export async function updateBugAssignee(
  bugReportId: string,
  assigneeUid: string | null,
): Promise<void> {
  await updateBugReport(bugReportId, { assigneeUid });
}

/** R-131-SUP-E — clear reporter unread badge. */
export async function markBugReportRead(bugReportId: string): Promise<void> {
  const res = await authedFetch(`/api/bug-reports/${encodeURIComponent(bugReportId)}/mark-read`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(await readErrorMessage(res, 'Mark read failed'));
}
